import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Sentry } from '@/lib/monitoring/sentry';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermissions } from '@/hooks/usePermissions';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorId?: string;
}

function RouteErrorFallback({ errorId, onRetry }: { errorId?: string; onRetry: () => void }) {
  const navigate = useNavigate();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const workspaceModule = useAppSelector((s) => s.auth.workspaceModule);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const { has } = usePermissions();

  const dashboardPath = resolveAccessibleLandingPath(
    {
      permissions,
      roleCode,
      enabledModules,
      accessibleModules,
      entryModule,
      hasPermission: has,
    },
    workspaceModule ?? entryModule,
  );

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center px-6 text-center">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Something went wrong</h2>
      <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
        We could not load this page. Try again or contact support if the problem continues.
      </p>
      {errorId && <p className="mt-2 text-xs text-slate-400">Reference: {errorId}</p>}
      <div className="mt-6 flex gap-2">
        <Button type="button" onClick={onRetry}>
          Retry
        </Button>
        <Button type="button" variant="secondary" onClick={() => navigate(dashboardPath)}>
          Go to dashboard
        </Button>
      </div>
    </div>
  );
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorId: crypto.randomUUID().slice(0, 8) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route error', error, info);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <RouteErrorFallback
          errorId={this.state.errorId}
          onRetry={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}
